import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface RunSummary {
  id: string;
  user_id: string | null;
  scope: string;
  status: string;
  started_at: number;
  finished_at: number | null;
  totals: { new_count: number; removed_count: number; changed_count: number };
  company_count: number;
  queued_count: number;
  running_count: number;
  done_count: number;
  error_count: number;
}

export interface RunCompany {
  company_id: string;
  company_name: string | null;
  tier: number | null;
  ats: string | null;
  status: string;
  new_count: number;
  removed_count: number;
  changed_count: number;
  error: string | null;
}

export interface RunDetail extends RunSummary {
  companies: RunCompany[];
}

export interface FailureStreak {
  company_id: string;
  company_name: string | null;
  error_count: number;
  window_size: number;
}

export interface CronHealth {
  last_run: RunSummary | null;
  expected_interval_ms: number;
  last_skew_ms: number | null;
  recent_runs: RunSummary[];
  failure_streaks: FailureStreak[];
}

export function useCronHealth() {
  return useQuery({
    queryKey: ["runs", "cron"],
    queryFn: () => api<CronHealth>("/api/runs/cron"),
    // Cron only fires every 6h — refresh once a minute is plenty.
    refetchInterval: 60_000,
  });
}

export function useRuns(limit = 20) {
  return useQuery({
    queryKey: ["runs", limit],
    queryFn: () => api<{ runs: RunSummary[] }>(`/api/runs?limit=${limit}`),
    refetchInterval: (q) => {
      // Auto-poll while any listed run is still queued/running.
      const data = q.state.data;
      if (!data) return false;
      const hot = data.runs.some(
        (r) => r.status === "running" || r.queued_count + r.running_count > 0,
      );
      return hot ? 3_000 : false;
    },
  });
}

export function useRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ["runs", "detail", runId],
    enabled: Boolean(runId),
    queryFn: () => api<RunDetail>(`/api/runs/${runId}`),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      return data.queued_count + data.running_count > 0 ? 2_000 : false;
    },
  });
}

export interface RunNowResult {
  run_id: string;
  scope: string;
  company_count: number;
  company_id: string;
  ats: string;
}

export interface RunNowError {
  status: number;
  error: string;
  retry_after_ms?: number;
  throttle_seconds?: number;
}

export function useRunNow() {
  const qc = useQueryClient();
  return useMutationToast<RunNowResult, RunNowError, string>({
    success: "Check enqueued — see /runs for progress.",
    errorPrefix: "Run failed",
    mutationFn: async (companyId: string) => {
      try {
        return await api<RunNowResult>(`/api/checks/${companyId}`, {
          method: "POST",
        });
      } catch (err) {
        if (err instanceof ApiError) {
          let payload: { error?: string; retry_after_ms?: number; throttle_seconds?: number } = {};
          try {
            payload = JSON.parse(err.message);
          } catch {
            // not JSON
          }
          throw {
            status: err.status,
            error: payload.error ?? err.message ?? "request_failed",
            retry_after_ms: payload.retry_after_ms,
            throttle_seconds: payload.throttle_seconds,
          } satisfies RunNowError;
        }
        throw { status: 0, error: "network_error" } satisfies RunNowError;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });
}
